import {
  pgTable,
  uuid,
  jsonb,
  boolean,
  pgEnum,
  index,
  integer,
  text,
  timestamp,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { identity_user } from '@trashlab/api/src/domains/identity/schemas/identity-user';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import type { PgAddress } from '@trashlab/api/src/domains/core/types';

export const task_status = pgEnum('task_status', ['TODO', 'DONE']);

export const comment = pgTable(
  'comment',
  {
    ...defaultFields.fieldsWithTenantId(),
    body: text('body').notNull(),
    content: jsonb('content').notNull(),
    createdBy: uuid('createdBy').references(() => identity_user.id),
    parentId: uuid('parentId'),
    taskStatus: task_status('taskStatus'),
    dueDate: timestamp('dueDate', {
      withTimezone: true,
      mode: 'string',
    }),
    userAssignedId: uuid('userAssignedId').references(() => identity_user.id),
    isPinned: boolean('isPinned').default(false),
    lastUpdatedBy: uuid('lastUpdatedBy').references(() => identity_user.id),
    isAiGenerated: boolean('isAiGenerated').default(false),
    color: varchar('color', { length: 7 }), // Hex color e.g. #FF5733, nullable
    title: text('title'), // Task title for better display
    priority: varchar('priority', { length: 16 }), // validated by Zod (commentPrioritySchema), not DB
    location: jsonb('location').$type<PgAddress>(), // PgAddress shape, validated by Zod (pgAddressSchema)
    addressString: text('addressString'), // Denormalized one-line address (mirrors customer_site.addressString)
    durationMinutes: integer('durationMinutes'), // Estimated task duration in minutes; app-layer Zod validation only
  },
  (table) => ({
    createdAtTs_idx: index('comment_createdAtTs_idx').on(table.createdAtTs),
    taskStatus_idx: index('comment_taskStatus_idx').on(table.taskStatus),
    parentId_idx: index('comment_parentId_idx').on(table.parentId),
    tenantId_idx: index('comment_tenantId_idx').on(table.tenantId),
    archivedAtTs_idx: index('comment_archivedAtTs_idx').on(table.archivedAtTs),
    tenant_archived_parent_idx: index('comment_tenant_archived_parent_idx').on(
      table.tenantId,
      table.archivedAtTs,
      table.parentId,
    ),
    // Composite sort used by fast-path query
    comment_tenant_parent_archived_isPinned_created_idx: index(
      'comment_tenant_parent_archived_isPinned_created_idx',
    ).on(
      table.tenantId,
      table.parentId,
      table.archivedAtTs,
      table.isPinned,
      table.createdAtTs,
    ),
    // Partial index for active top-level comments by tenant
    comment_active_by_tenant_isPinned_created_idx: index(
      'comment_active_by_tenant_isPinned_created_idx',
    )
      .on(table.tenantId, table.isPinned, table.createdAtTs)
      .where(sql`"parentId" IS NULL AND "archivedAtTs" IS NULL`),
    nullable_archived_parent_idx: index('comment_nullable_archived_parent_idx')
      .on(table.archivedAtTs, table.parentId)
      .where(sql`"archivedAtTs" IS NULL AND "parentId" IS NULL`),
    task_due_date_reminder_candidate_idx: index(
      'comment_task_due_date_reminder_candidate_idx',
    )
      .on(table.dueDate, table.id)
      .where(
        sql`"archivedAtTs" IS NULL AND "taskStatus" = 'TODO' AND "dueDate" IS NOT NULL AND "userAssignedId" IS NOT NULL`,
      ),
    taskUsageGlobalRecentIdx: index('comment_task_usage_global_recent_idx')
      .on(table.createdAtTs.desc(), table.id.desc())
      .where(sql`${table.taskStatus} IS NOT NULL AND ${table.parentId} IS NULL`),
    taskUsageTenantRecentIdx: index('comment_task_usage_tenant_recent_idx')
      .on(table.tenantId, table.createdAtTs.desc(), table.id.desc())
      .where(sql`${table.taskStatus} IS NOT NULL AND ${table.parentId} IS NULL`),
  }),
);

export const comment_history = pgTable('comment_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  body: text('body'),
  content: jsonb('content'),
  createdBy: uuid('createdBy'),
  parentId: uuid('parentId'),
  taskStatus: task_status('taskStatus'),
  dueDate: timestamp('dueDate', {
    withTimezone: true,
    mode: 'string',
  }),
  userAssignedId: uuid('userAssignedId'),
  isPinned: boolean('isPinned'),
  lastUpdatedBy: uuid('lastUpdatedBy'),
  isAiGenerated: boolean('isAiGenerated'),
  color: varchar('color', { length: 7 }),
  title: text('title'),
  priority: varchar('priority', { length: 16 }),
  location: jsonb('location').$type<PgAddress>(),
  addressString: text('addressString'),
  durationMinutes: integer('durationMinutes'),
});
